import { Stack } from '@mui/material';
import { debounce, find, get } from 'lodash';
import React, { Component } from 'react';
import { AppContext } from '../../common/AppContext';
import { ErrorModel } from '../../common/models/ErrorModel';
import { ChipContainer } from '../../components/ChipContainer';
import { Dialog } from '../../components/dialogs/Dialog';
import { MultiAttributeSelector } from '../../components/selectors/MultiAttributeSelector';
import { PAGE_SIZE_FOR_ADD_EDIT } from '../../constants';
import { Data } from '../../data/Data';
import { SettingsAttribute } from '../../data/settings/SettingsAttribute';
import { SettingsAttributeList } from '../../data/settings/SettingsAttributeList';
import { withLocale, WithLocaleProps } from '../../providers/LocaleProvider';

interface AttributeSelectorDialogProps extends WithLocaleProps {
  theme?: any;
  organization?: any;
  user?: any;
  error?: string;
  showSuggestions?: boolean;
  jobDefinitionId?: string;
  excludedAttributes?: any[];
  allowChildren?: boolean;
  onClose?: () => void;
  onSave?: (attributes: any[]) => void;
  onShowNewAttributeDialog?: () => void;
  onLoadAttributes?: (loading: boolean) => void;
}

class AttributeSelectorDialogBase extends Component<AttributeSelectorDialogProps, any> {
  static contextType = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  constructor(props: any) {
    super(props);

    this.state = {
      attributes: null,
      selectedAttributes: [],
      selectedSuggestedAttributes: [],
      allSelectedAttributes: [],
      suggestedAttributes: [],
      allowChildren: true,
      attributesSearch: null,
      isAttributeLoading: false,
    };

    this.onSearchAttributes = debounce(this.onSearchAttributes.bind(this), 500);
  }

  componentDidMount() {
    const { onLoadAttributes, showSuggestions, jobDefinitionId } = this.props;
    // if (onLoadAttributes) onLoadAttributes(true);
    this.loadAttributes();

    if (showSuggestions && jobDefinitionId) this.loadSuggestedAttributes(jobDefinitionId);
  }

  loadSuggestedAttributes(jobDefinitionId: any) {
    this.context.setLoading('loading', true);

    (Data as any)
      .get(this.context, '/JobDefinition/' + jobDefinitionId, {
        withCredentials: true,
      })
      .then((response: any) => {
        const suggestedAttributes: any = [];
        response.data.jobDefinition.jobAttributes.forEach((a: any) => {
          const attr = new SettingsAttribute(a);
          suggestedAttributes.push(attr);
        });
        this.setState({ suggestedAttributes });
      })
      .catch((e: any) => {
        this.setState({ error: ErrorModel.parseServerError(e) });
      })
      .finally(() => {
        this.context.setLoading('loading', false);
      });
  }

  loadAttributes(searchText?: any) {
    this.context.setLoading('attributes', true);
    this.setState({ isAttributeLoading: true });

    const pagingOptions = {
      pageNumber: 1,
      pageSize: PAGE_SIZE_FOR_ADD_EDIT,
      searchText: searchText,
    };

    // get setting attributes data then set to attributes state
    SettingsAttributeList.getWithPaging(this.context, pagingOptions)
      .then((attributes) => {
        this.setState({ attributes, attributesSearch: searchText });
      })
      .catch((error) => {})
      .finally(() => {
        this.context.setLoading('attributes', false);
        this.setState({ isAttributeLoading: false });
      });
  }

  onAttributesBlur() {
    const { attributesSearch } = this.state;

    if (attributesSearch) this.loadAttributes();
  }

  onSearchAttributes(searchText: any) {
    this.loadAttributes(searchText);
  }

  onHandleSearchAttributes(searchText: any) {
    this.setState({ attributes: [], isAttributeLoading: true });
    this.onSearchAttributes(searchText);
  }

  onSave() {
    const { onSave } = this.props;
    const { allSelectedAttributes } = this.state;
    if (onSave) onSave(allSelectedAttributes);
  }

  onAttributesChange(e: any, selectedAttributes: any) {
    const { selectedSuggestedAttributes, suggestedAttributes } = this.state;
    const findSuggestion = (a: any) =>
      suggestedAttributes.find((s: any) => s.attributeDefinitionId === a.attributeDefinitionId);

    // A competency picked from search that is also suggested ticks its chip instead, so it is
    // selected in one place only. The chip moves to the front so it is visible when collapsed.
    const pickedSuggestions = selectedAttributes.map(findSuggestion).filter(Boolean);
    const remainingAttributes = selectedAttributes.filter((a: any) => !findSuggestion(a));
    const newSelectedSuggestedAttributes = [
      ...selectedSuggestedAttributes,
      ...pickedSuggestions.filter((s: any) => !selectedSuggestedAttributes.includes(s)),
    ];

    this.setAttributes([...newSelectedSuggestedAttributes, ...remainingAttributes]);
    this.setState({
      selectedAttributes: remainingAttributes,
      selectedSuggestedAttributes: newSelectedSuggestedAttributes,
      suggestedAttributes: [
        ...pickedSuggestions,
        ...suggestedAttributes.filter((s: any) => !pickedSuggestions.includes(s)),
      ],
    });
  }

  onSuggestedAttributesChange(e: any, selectedSuggestedAttributes: any) {
    const { selectedAttributes } = this.state;
    this.setAttributes([...selectedAttributes, ...selectedSuggestedAttributes]);
    this.setState({ selectedSuggestedAttributes });
  }

  setAttributes(allSelectedAttributes: any) {
    allSelectedAttributes.forEach((a: any) => (a.type = null));
    this.setState({ allSelectedAttributes });
  }

  render() {
    const {
      organization,
      user,
      theme,
      onClose,
      error,
      onShowNewAttributeDialog,
      showSuggestions,
      excludedAttributes,
      allowChildren,
      translate,
    } = this.props;
    const {
      attributes,
      allSelectedAttributes,
      selectedAttributes,
      suggestedAttributes,
      selectedSuggestedAttributes,
      isAttributeLoading,
    } = this.state;

    let suggestedAttributesFiltered: any = null;
    if (showSuggestions)
      suggestedAttributesFiltered = suggestedAttributes.filter(
        (a: any) =>
          !excludedAttributes?.find(
            (a2: any) => a2.attributeDefinitionId === a.attributeDefinitionId
          )
      );

    let attributesFiltered = get(attributes, 'attributes', []);

    if (attributes) {
      if (excludedAttributes) {
        attributesFiltered = attributesFiltered.filter(
          (a: any) =>
            !find(
              excludedAttributes,
              (a2) => get(a2, 'attributeDefinitionId') === get(a, 'attributeDefinitionId')
            )
        );
      }
    }

    if (!allowChildren) {
      attributesFiltered = attributesFiltered.filter((a: any) => !get(a, 'isChild'));
    }

    return (
      <Dialog
        open={true}
        theme={theme}
        onClose={onClose}
        fullWidth={true}
        maxWidth='sm'
        title={translate('dialogs.addAttributes')}
        actions={[
          {
            label: translate('dialogs.addAttributes'),
            primary: true,
            disabled: allSelectedAttributes.length === 0,
            onClick: this.onSave.bind(this),
          },
          {
            label: translate('common.cancel'),
            onClick: onClose,
          },
        ]}
      >
        <Stack direction='column' spacing={4}>
          {showSuggestions &&
          suggestedAttributesFiltered &&
          suggestedAttributesFiltered.length > 0 ? (
            <Stack direction='column' spacing={4}>
              <div>{translate('dialogs.suggestedCompetencies')}</div>
              <ChipContainer
                items={suggestedAttributesFiltered}
                selectedItems={selectedSuggestedAttributes}
                onChange={this.onSuggestedAttributesChange.bind(this)}
              />
            </Stack>
          ) : (
            <div>
              {translate('dialogs.selectCompetencies', {
                target: showSuggestions
                  ? translate('entities.employee').toLowerCase()
                  : translate('entities.job').toLowerCase(),
              })}
            </div>
          )}

          <MultiAttributeSelector
            label={null}
            items={attributesFiltered}
            values={selectedAttributes}
            onNewItemClick={onShowNewAttributeDialog}
            getItemId={(attr: any) => attr.attributeId}
            onChange={this.onAttributesChange.bind(this)}
            onSearch={(searchText: any) => {
              this.onHandleSearchAttributes(searchText);
            }}
            onBlur={this.onAttributesBlur.bind(this)}
            translate={translate}
            isLoading={isAttributeLoading}
          />
        </Stack>
      </Dialog>
    );
  }
}

export const AttributeSelectorDialog = withLocale(AttributeSelectorDialogBase);
